import { Button, Checkbox, Group, Modal, NumberInput, Select, Stack, Text } from '@mantine/core';
import type { Practitioner } from '@medplum/fhirtypes';
import { useEffect, useState } from 'react';
import { referralPreferencesFromPractitioner } from './referralPreferencesFromPractitioner';

export interface ReferralRequestCreateValues {
  requesterPractitionerId: string;
  numberOfClients: number;
  acceptsInPerson: boolean;
  noMinors: boolean;
  noCouplesFamilies: boolean;
  onlyCashPay: boolean;
}

export interface ReferralRequestCreateModalProps {
  opened: boolean;
  onClose: () => void;
  onSubmit: (values: ReferralRequestCreateValues) => void | Promise<void>;
  providerOptions?: { value: string; label: string }[];
  providersById?: Record<string, Practitioner | undefined>;
  /** When set, the provider select is locked to this id. */
  lockedPractitionerId?: string;
}

export function ReferralRequestCreateModal({
  opened,
  onClose,
  onSubmit,
  providerOptions = [],
  providersById = {},
  lockedPractitionerId,
}: ReferralRequestCreateModalProps): JSX.Element {
  const defaultProviderId = lockedPractitionerId ?? providerOptions[0]?.value ?? '';
  const [providerId, setProviderId] = useState(defaultProviderId);
  const [numberOfClients, setNumberOfClients] = useState(1);
  const [acceptsInPerson, setAcceptsInPerson] = useState(false);
  const [noMinors, setNoMinors] = useState(false);
  const [noCouplesFamilies, setNoCouplesFamilies] = useState(false);
  const [onlyCashPay, setOnlyCashPay] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!opened) return;
    const nextId = lockedPractitionerId ?? providerOptions[0]?.value ?? '';
    setProviderId(nextId);
    setNumberOfClients(1);
    const prefs = referralPreferencesFromPractitioner(providersById[nextId]);
    setAcceptsInPerson(prefs.acceptsInPerson);
    setNoMinors(prefs.noMinors);
    setNoCouplesFamilies(prefs.noCouplesFamilies);
    setOnlyCashPay(prefs.onlyCashPay);
  }, [opened, lockedPractitionerId, providerOptions, providersById]);

  const applyProvider = (nextId: string | null): void => {
    if (!nextId) return;
    setProviderId(nextId);
    const prefs = referralPreferencesFromPractitioner(providersById[nextId]);
    setAcceptsInPerson(prefs.acceptsInPerson);
    setNoMinors(prefs.noMinors);
    setNoCouplesFamilies(prefs.noCouplesFamilies);
    setOnlyCashPay(prefs.onlyCashPay);
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Request new clients" size="md" centered>
      <Stack gap="lg">
        {lockedPractitionerId ? null : (
          <Select
            label="Provider"
            description="Who is requesting additional client referrals?"
            placeholder={providerOptions.length === 0 ? 'No providers available' : 'Select provider'}
            data={providerOptions}
            value={providerId || null}
            onChange={applyProvider}
            searchable
            nothingFoundMessage="No providers found"
            disabled={providerOptions.length === 0}
            required
          />
        )}
        <NumberInput
          label="Number of clients needed"
          description="How many referral slots do you need?"
          value={numberOfClients}
          onChange={(value) => setNumberOfClients(Number(value) || 1)}
          min={1}
          max={50}
        />
        <Stack gap="xs">
          <Text fw={600}>Scheduling preferences</Text>
          <Checkbox
            label="I am taking in-person clients."
            checked={acceptsInPerson}
            onChange={(event) => setAcceptsInPerson(event.currentTarget.checked)}
            color="rgb(191, 163, 90)"
          />
        </Stack>
        <Stack gap="xs">
          <Text fw={600}>Client preferences</Text>
          <Checkbox
            label="No minors"
            checked={noMinors}
            onChange={(event) => setNoMinors(event.currentTarget.checked)}
            color="rgb(191, 163, 90)"
          />
          <Checkbox
            label="No couple's or families"
            checked={noCouplesFamilies}
            onChange={(event) => setNoCouplesFamilies(event.currentTarget.checked)}
            color="rgb(191, 163, 90)"
          />
          <Checkbox
            label="Only cash-pay clients"
            checked={onlyCashPay}
            onChange={(event) => setOnlyCashPay(event.currentTarget.checked)}
            color="rgb(191, 163, 90)"
          />
        </Stack>
        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={submitting}
            onClick={() => {
              if (!providerId) return;
              setSubmitting(true);
              void Promise.resolve(
                onSubmit({
                  requesterPractitionerId: providerId,
                  numberOfClients,
                  acceptsInPerson,
                  noMinors,
                  noCouplesFamilies,
                  onlyCashPay,
                }),
              ).finally(() => setSubmitting(false));
            }}
          >
            Submit request
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
